import { useEffect } from "react";
import { ReactNode, useState } from "react";
import { createContext } from "react";
import useAppData from "../../../../../hook/useAppData";
import { convertDate, dateToInitial, _formatDate } from "../../../../../libs/dateUtils";
import { orderBy } from "../../../../../libs/orderBy";
import { orderByName } from "../../../../../libs/orderList";
import { postNewPassword } from "../../../../../page/auth/service/service";
import { DataCollaborator, UserScheduleOptimization } from "../../../model";
import { serviceUsers } from "../../../service";
import { CASES_FILTER_COLLABORATORS } from "../constants";
import searchTheEntireList from "../lib/searchFilterList";

interface StoreProviderProps {
  children: ReactNode;
}

interface GridCollaboratorContextProps {
  emptyFilter: any;
  needSpinner: any;
  needModalDelete: any;
  needModalPassword: any;
  filterValue: any;
  selectFilter: any;
  inactiveOrActive: any;
  collaboratorsOriginal: any;
  collaboratorsDeleted: any;
  collaboratorActivated: any;
  csvDataInactived: any;
  csvDataActived: any;
  page: any;
  setPage: any;
  selectSize: any;
  setNeedModalDelete: any;
  handleDeleteClick: any;
  setNeedModalPassword: any;
  handlePasswordClick: any;
  setFilterValue: any;
  setSelectFilter: any;
  handleInputFilterCollaborator: any;
  setStateFunction: any;
  handleActiveClick: any;
  setSelectSize: any;
  needModalPopUp: any;
  setNeedModalPopUp: any;
  schedulesNexts: UserScheduleOptimization[];
  handleOrderList: (label: string) => void;
  saveValuesFilter: (p: any[]) => any[];
}

export const GridCollaboratorContext = createContext({} as GridCollaboratorContextProps);

export function GridCollaboratorProvider({ children }: StoreProviderProps) {
  const { token } = useAppData();

  const [emptyFilter, setEmptyFilter] = useState(false);
  const [needSpinner, setNeedSpinner] = useState(true);
  const [needModalDelete, setNeedModalDelete] = useState({
    status: false,
    id: 0,
  });
  const [needModalPopUp, setNeedModalPopUp] = useState({
    status: false,
    id: 0,
    listRender: {},
  });
  const [needModalPassword, setNeedModalPassword] = useState({
    status: false,
    email: "",
  });

  const [filterValue, setFilterValue] = useState("");
  const [selectFilter, setSelectFilter] = useState(1);
  const [inactiveOrActive, setInactiveOrActive] = useState(false);

  const [collaboratorsOriginal, setCollaboratorsOriginal] = useState([] as DataCollaborator[]);
  const [collaboratorsDeleted, setcollaboratorsDeleted] = useState([] as DataCollaborator[]);
  const [collaboratorActivated, setcollaboratorActivated] = useState([] as DataCollaborator[]);
  const [csvDataInactived, setCsvDataInactived] = useState([]);
  const [csvDataActived, setCsvDataActived] = useState([]);
  const [page, setPage] = useState(1);
  const [selectSize, setSelectSize] = useState(10);
  const [schedulesNexts, setSchedulesNexts] = useState<UserScheduleOptimization[]>();
  const [orderSwitch, setOrderSwitch] = useState(true);

  useEffect(() => {
    getUsersApi();
    getSchedulesNexts();
    handleCsvClick();
  }, []);

  useEffect(() => {
    setNeedSpinner(true);
    setTimeout(() => {
      setNeedSpinner(false);
    }, 500);
  }, [collaboratorActivated, collaboratorsDeleted, inactiveOrActive, selectSize]);

  async function getSchedulesNexts() {
    const res = await serviceUsers.getUserScheduleNext(token);
    setSchedulesNexts(res);
  }

  function getUsersApi() {
    serviceUsers.getUsers(token).then((data: DataCollaborator[]) => {
      const cleanerData = cleanData(data);
      const [collaboratorActive, collaboratorInactivated] = separateActiveFromInactiveCollaborator(cleanerData);
      setCollaboratorsOriginal(cleanerData);
      const collaboratoActiveOrderned = orderByName(collaboratorActive, "name");
      const collaboratoInactivedOrderned = orderByName(collaboratorInactivated, "name");
      generateCsvDownload({ active: collaboratorActivated, inactive: collaboratorInactivated });
      setcollaboratorsDeleted(collaboratoInactivedOrderned || collaboratorInactivated);
      setcollaboratorActivated(collaboratoActiveOrderned || collaboratorActive);
    });
  }

  function handleOrderList(label: string) {
    orderSwitch
      ? setcollaboratorActivated(orderBy(collaboratorActivated, label, "asc"))
      : setcollaboratorActivated(orderBy(collaboratorActivated, label, "desc"));
    orderSwitch
      ? setcollaboratorsDeleted(orderBy(collaboratorsDeleted, label, "asc"))
      : setcollaboratorsDeleted(orderBy(collaboratorsDeleted, label, "desc"));

    setOrderSwitch((oldState: boolean) => !oldState);

    setPage(0);
  }

  function cleanData(data: DataCollaborator[]): DataCollaborator[] {
    return data?.map((objeto: DataCollaborator) => {
      return {
        ...objeto,
        job_role: cleanFields(objeto.job_role),
        technology: cleanFields(objeto.technology),
      };
    });
  }

  function cleanFields(data: any): any[] {
    if (!data?.length) return [];
    return data?.map((field: any) => {
      return field?.name;
    });
  }

  function separateActiveFromInactiveCollaborator(list?: DataCollaborator[]) {
    const listCollaborator = list || collaboratorsOriginal;
    const active = listCollaborator?.filter((user) => Boolean(!user.isDeleted));
    const inactive = listCollaborator?.filter((user) => Boolean(user.isDeleted));
    return [active, inactive];
  }

  async function handleInputFilterCollaborator(value?: string) {
    const sanitizeValue = typeof value === "string" ? value : undefined;
    const listOriginal = collaboratorsOriginal;
    const filterList = searchTheEntireList({
      initialCase: selectFilter - 1,
      list: listOriginal,
      cases: CASES_FILTER_COLLABORATORS,
      value: sanitizeValue || filterValue,
    });
    const [filterActive, filterInactive] = saveValuesFilter(filterList);
    generateCsvDownload({ active: filterActive, inactive: filterInactive });
    displayList({ active: filterActive, inactive: filterInactive });
  }

  function saveValuesFilter(filterList: any[]) {
    const [filterActive, filterInactive] = separateActiveFromInactiveCollaborator(filterList);
    setcollaboratorsDeleted(filterInactive);
    setcollaboratorActivated(filterActive);
    return [filterActive, filterInactive];
  }

  async function generateCsvDownload({ active: filterActive, inactive: filterInactive }: any) {
    const [dataActive, dataInactive] = await Promise.all([handleCsvClick(filterActive), handleCsvClick(filterInactive)]);

    setCsvDataActived(dataActive);
    setCsvDataInactived(dataInactive);
  }

  function displayList({ active: filterActive, inactive: filterInactive }: any) {
    const displayList = inactiveOrActive ? filterInactive : filterActive;
    if (displayList?.length) return setEmptyFilter(false);
    return setEmptyFilter(true);
  }

  function handleDeleteClick(id: number) {
    setNeedSpinner(true);
    serviceUsers.getUsersById(token, id).then((res: any) => {
      const data = {
        ...res,
        end_date: convertDate(_formatDate(dateToInitial)),
        isDeleted: !res.IsDeleted,
      };
      serviceUsers.putUsers(data, id, token).then(() => {
        getUsersApi();
      });
    });
  }

  function handleActiveClick(id: any) {
    setNeedSpinner(true);
    serviceUsers.getUsersById(token, id).then((res: any) => {
      const data = {
        ...res,
        end_date: "",
        isDeleted: !res.isDeleted,
      };
      serviceUsers.putUsers(data, id, token).then(() => {
        getUsersApi();
      });
    });
  }

  function setStateFunction() {
    setNeedSpinner(true);
    setInactiveOrActive((oldState) => !oldState);
  }

  function handlePasswordClick(email: string) {
    const data = {
      email: email,
    };
    postNewPassword(data);
  }

  async function handleCsvClick(list?: any[]) {
    let query = "";
    if (list?.length) {
      const userIds = searchUserId(list);
      query = queryStringGenerator(userIds);
    }
    const data = await serviceUsers.csvUserDowloan({
      token: token,
      query: query,
    });
    return data;
  }

  function searchUserId(list: DataCollaborator[]): string[] {
    return list?.map((user) => String(user?.id)) || [];
  }

  function queryStringGenerator(ids?: string[]): string {
    let query = "";
    if (ids?.length) {
      query += `?id=${ids.join()}`;
    }
    return query;
  }

  return (
    <GridCollaboratorContext.Provider
      value={{
        emptyFilter,
        needSpinner,
        needModalPopUp,
        setNeedModalPopUp,
        needModalDelete,
        needModalPassword,
        filterValue,
        selectFilter,
        inactiveOrActive,
        collaboratorsOriginal,
        collaboratorsDeleted,
        collaboratorActivated,
        csvDataInactived,
        csvDataActived,
        page,
        selectSize,
        setNeedModalDelete,
        handleDeleteClick,
        setNeedModalPassword,
        handlePasswordClick,
        setFilterValue,
        setSelectFilter,
        handleInputFilterCollaborator,
        setStateFunction,
        handleActiveClick,
        setPage,
        setSelectSize,
        schedulesNexts,
        handleOrderList,
        saveValuesFilter,
      }}
    >
      {children}
    </GridCollaboratorContext.Provider>
  );
}
